<template>
  <div class="notification-page">
    <!-- Background Image with blur & transparency -->
    <div class="background-image"></div>

    <div class="notification-container">
      <!-- ========================================== -->
      <!-- HEADER -->
      <!-- ========================================== -->
      <div class="notification-header">
        <div class="header-left">
          <h4 class="mb-0">
            <b-icon icon="bell" class="header-icon" />
            Notifications
          </h4>
          <span class="header-count" v-if="unreadCount > 0">
            {{ unreadCount }} unread
          </span>
        </div>
        <div class="header-right">
          <b-button
            v-if="unreadCount > 0"
            variant="primary"
            size="sm"
            @click="markAllAsRead"
            class="mr-2"
          >
            <b-icon icon="check2-all" /> Mark all as read
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="refreshNotifications"
          >
            <b-icon icon="arrow-clockwise" />
          </b-button>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- BODY -->
      <!-- ========================================== -->
      <div class="notification-body">
        <!-- ========================================== -->
        <!-- LEFT SIDEBAR -->
        <!-- ========================================== -->
        <div class="notification-sidebar">
          <div class="sidebar-section">
            <div class="sidebar-title">Inbox</div>
            <div
              class="sidebar-item"
              :class="{ active: selectedType === 'all' }"
              @click="selectType('all')"
            >
              <b-icon icon="inbox" class="sidebar-icon" />
              <span>All</span>
              <span class="badge-count" v-if="totalCount > 0">{{ totalCount }}</span>
            </div>
            <div
              class="sidebar-item"
              :class="{ active: selectedType === 'unread' }"
              @click="selectType('unread')"
            >
              <b-icon icon="envelope" class="sidebar-icon" />
              <span>Unread</span>
              <span class="badge-count danger" v-if="unreadCount > 0">{{ unreadCount }}</span>
            </div>
          </div>

          <div class="sidebar-divider"></div>

          <div class="sidebar-section">
            <div class="sidebar-title">Categories</div>
            <div
              v-for="type in notificationTypes"
              :key="type.type"
              class="sidebar-item"
              :class="{ active: selectedType === type.type }"
              @click="selectType(type.type)"
            >
              <b-icon :icon="getTypeIcon(type.type)" class="sidebar-icon" />
              <span>{{ getTypeLabel(type.type) }}</span>
              <span class="badge-count" v-if="type.count > 0">{{ type.count }}</span>
            </div>
          </div>

          <div class="sidebar-divider"></div>

          <div class="sidebar-section">
            <div class="sidebar-item" @click="showPreferences = !showPreferences">
              <b-icon icon="gear" class="sidebar-icon" />
              <span>Settings</span>
              <b-icon icon="chevron-right" class="sidebar-arrow" :class="{ rotated: showPreferences }" />
            </div>

            <!-- Preferences -->
            <div v-if="showPreferences" class="preferences-panel">
              <div class="pref-item">
                <label class="pref-label">
                  <input type="checkbox" v-model="preferences.email_notifications" />
                  Email notifications
                </label>
              </div>
              <div class="pref-item">
                <label class="pref-label">
                  <input type="checkbox" v-model="preferences.inventory_alerts" />
                  Inventory alerts
                </label>
              </div>
              <div class="pref-item">
                <label class="pref-label">
                  <input type="checkbox" v-model="preferences.order_alerts" />
                  Order alerts
                </label>
              </div>
              <div class="pref-item">
                <label class="pref-label">
                  <input type="checkbox" v-model="preferences.refund_alerts" />
                  Refund & return alerts
                </label>
              </div>
              <div class="pref-item">
                <label class="pref-label">
                  <input type="checkbox" v-model="preferences.system_alerts" />
                  System alerts
                </label>
              </div>
              <b-button size="sm" variant="primary" block @click="savePreferences" class="mt-2">
                Save Preferences
              </b-button>
            </div>
          </div>
        </div>

        <!-- ========================================== -->
        <!-- RIGHT - NOTIFICATION LIST -->
        <!-- ========================================== -->
        <div class="notification-list">
          <!-- Loading -->
          <div v-if="loading" class="list-loading">
            <b-spinner variant="primary" />
            <p>Loading notifications...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="notifications.length === 0" class="list-empty">
            <b-icon icon="bell-slash" scale="3" class="empty-icon" />
            <h5>No notifications</h5>
            <p class="text-muted">You're all caught up!</p>
          </div>

          <!-- Notifications -->
          <div v-else class="list-items">
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="notification-item-wrapper"
              :class="{ unread: !notification.is_read }"
            >
              <div class="notification-item">
                <!-- Avatar / Icon -->
                <div class="notification-icon">
                  <div class="icon-circle" :class="getTypeClass(notification.type)">
                    <b-icon :icon="getTypeIcon(notification.type)" />
                  </div>
                </div>

                <!-- Content -->
                <div class="notification-content" @click="openAction(notification)">
                  <div class="notification-header-content">
                    <div class="notification-title">
                      <span class="title-text">{{ notification.title }}</span>
                      <span v-if="!notification.is_read" class="unread-badge">New</span>
                      <span class="priority-badge" :class="notification.priority">
                        {{ notification.priority }}
                      </span>
                    </div>
                    <span class="notification-time">{{ notification.time_ago }}</span>
                  </div>

                  <div class="notification-message">{{ notification.message }}</div>

                  <!-- <div class="notification-footer">
                    <span class="notification-type-label">
                      <span class="type-badge" :class="notification.type">
                        {{ getTypeLabel(notification.type) }}
                      </span>
                    </span>
                    <div class="notification-actions">
                      <button
                        v-if="notification.action_url"
                        class="action-btn"
                        @click.stop="openAction(notification)"
                      >
                        {{ notification.action_text || 'View' }}
                        <b-icon icon="chevron-right" />
                      </button>
                      <b-dropdown size="sm" variant="link" no-caret class="dropdown-actions" right>
                        <template #button-content>
                          <b-icon icon="three-dots" />
                        </template>
                        <b-dropdown-item @click.stop="markAsRead(notification.id)" v-if="!notification.is_read">
                          <b-icon icon="check" /> Mark as read
                        </b-dropdown-item>
                        <b-dropdown-item @click.stop="archiveNotification(notification.id)" v-if="!notification.is_archived">
                          <b-icon icon="archive" /> Archive
                        </b-dropdown-item>
                        <b-dropdown-item @click.stop="deleteNotification(notification.id)">
                          <b-icon icon="trash" /> Delete
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </div> -->
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="totalCount > limit" class="list-pagination">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCount"
              :per-page="limit"
              size="sm"
              align="center"
              @change="loadNotifications"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NotificationPage',
  data() {
    return {
      notifications: [],
      notificationTypes: [],
      selectedType: 'all',
      unreadCount: 0,
      loading: false,
      showPreferences: false,
      preferences: {
        email_notifications: true,
        inventory_alerts: true,
        order_alerts: true,
        refund_alerts: true,
        system_alerts: true
      },
      currentPage: 1,
      limit: 20,
      totalCount: 0,
      pollInterval: null
    };
  },

  created() {
    this.loadNotifications();
    this.loadNotificationTypes();
    this.loadUnreadCount();
    this.loadPreferences();
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    async loadNotifications() {
      this.loading = true;
      try {
        const params = {
          limit: this.limit,
          offset: (this.currentPage - 1) * this.limit
        };

        if (this.selectedType === 'unread') {
          params.is_read = 'false';
        } else if (this.selectedType !== 'all') {
          params.type = this.selectedType;
        }

        const response = await axios.get('/api/notification/notifications/', {
          params: params,
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.notifications = response.data.notifications;
          this.totalCount = response.data.total_count;
          this.unreadCount = response.data.unread_count;
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
        this.$bvToast.toast('Failed to load notifications', { variant: 'danger' });
      } finally {
        this.loading = false;
      }
    },

    async loadNotificationTypes() {
      try {
        const response = await axios.get('/api/notification/types/', {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.notificationTypes = response.data.types;
        }
      } catch (error) {
        console.error('Failed to load notification types:', error);
      }
    },

    async loadUnreadCount() {
      try {
        const response = await axios.get('/api/notification/notifications/unread/count/', {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.unreadCount = response.data.unread_count || 0;
          this.$root.$emit('unread-count-updated', this.unreadCount);
        }
      } catch (error) {
        console.error('Failed to load unread count:', error);
      }
    },

    async loadPreferences() {
      try {
        const response = await axios.get('/api/notification/preferences/', {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.preferences = response.data.preferences;
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      }
    },

    async markAsRead(notificationId) {
      try {
        const response = await axios.post(`/api/notification/notifications/${notificationId}/read/`, {}, {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.loadNotifications();
          this.loadUnreadCount();
          this.$bvToast.toast('Marked as read', { variant: 'success' });
        }
      } catch (error) {
        console.error('Failed to mark as read:', error);
        this.$bvToast.toast('Failed to mark as read', { variant: 'danger' });
      }
    },

    async markAllAsRead() {
      try {
        const response = await axios.post('/api/notification/notifications/read/all/', {}, {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.loadNotifications();
          this.loadUnreadCount();
          this.$bvToast.toast(`Marked ${response.data.marked_count} notifications as read`, { variant: 'success' });
        }
      } catch (error) {
        console.error('Failed to mark all as read:', error);
        this.$bvToast.toast('Failed to mark all as read', { variant: 'danger' });
      }
    },

    async archiveNotification(notificationId) {
      try {
        const response = await axios.post(`/api/notification/notifications/${notificationId}/archive/`, {}, {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.loadNotifications();
          this.$bvToast.toast('Archived', { variant: 'success' });
        }
      } catch (error) {
        console.error('Failed to archive:', error);
        this.$bvToast.toast('Failed to archive', { variant: 'danger' });
      }
    },

    async deleteNotification(notificationId) {
      this.$bvModal.msgBoxConfirm('Delete this notification?', {
        title: 'Delete',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel'
      }).then(async (confirm) => {
        if (confirm) {
          try {
            const response = await axios.delete(`/api/notification/notifications/${notificationId}/`, {
              headers: { Authorization: `Bearer ${localStorage.userToken}` }
            });

            if (response.data.success) {
              this.loadNotifications();
              this.loadUnreadCount();
              this.$bvToast.toast('Deleted', { variant: 'success' });
            }
          } catch (error) {
            console.error('Failed to delete:', error);
            this.$bvToast.toast('Failed to delete', { variant: 'danger' });
          }
        }
      });
    },

    async savePreferences() {
      try {
        const response = await axios.post('/api/notification/preferences/', this.preferences, {
          headers: { Authorization: `Bearer ${localStorage.userToken}` }
        });

        if (response.data.success) {
          this.$bvToast.toast('Preferences saved', { variant: 'success' });
          this.showPreferences = false;
        }
      } catch (error) {
        console.error('Failed to save preferences:', error);
        this.$bvToast.toast('Failed to save preferences', { variant: 'danger' });
      }
    },

    selectType(type) {
      this.selectedType = type;
      this.currentPage = 1;
      this.loadNotifications();
    },

    refreshNotifications() {
      this.loadNotifications();
      this.loadUnreadCount();
    },

    openAction(notification) {
      if (notification.action_url) {
        this.$router.push(notification.action_url);
        if (!notification.is_read) {
          this.markAsRead(notification.id);
        }
      }
    },

    getTypeLabel(type) {
      const labels = {
        'inventory': 'Inventory',
        'order': 'Order',
        'refund': 'Refund',
        'return': 'Return',
        'system': 'System'
      };
      return labels[type] || type;
    },

    getTypeIcon(type) {
      const icons = {
        'inventory': 'box-seam',
        'order': 'cart',
        'refund': 'arrow-counterclockwise',
        'return': 'arrow-return-left',
        'system': 'gear'
      };
      return icons[type] || 'bell';
    },

    getTypeClass(type) {
      const classes = {
        'inventory': 'type-inventory',
        'order': 'type-order',
        'refund': 'type-refund',
        'return': 'type-return',
        'system': 'type-system'
      };
      return classes[type] || 'type-system';
    },

    startPolling() {
      this.pollInterval = setInterval(() => {
        this.loadUnreadCount();
      }, 30000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    }
  }
};
</script>

<style scoped>
/* ============================================
   BACKGROUND IMAGE (blurred, transparent)
   ============================================ */
.background-image {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(8px);
  opacity: 0.7;
  z-index: -1;
  transform: scale(1.1);
  transition: filter 0.3s ease;
}

/* ============================================
   MAIN CONTAINER – glassmorphism, transparent
   ============================================ */
.notification-page {
  padding: 24px;
  min-height: 100vh;
  position: relative;
}

.notification-container {
  max-width: 1400px;
  margin: 0 auto;      /* semi-transparent */
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

/* ============================================
   HEADER – glass effect
   ============================================ */
.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px;
  background: rgba(244, 247, 252, 0.6);
  backdrop-filter: blur(4px);
  border-bottom: 1px solid rgba(220, 226, 236, 0.4);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-left h4 {
  font-weight: 700;
  color: #0b1a30;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  margin: 0;
}

.header-icon {
  color: #4a6cf7;
  font-size: 1.4rem;
}

.header-count {
  background: rgba(220, 229, 245, 0.7);
  color: #1f3a7c;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 16px;
  border-radius: 30px;
  backdrop-filter: blur(2px);
}

.header-right .btn {
  font-size: 14px;
  padding: 6px 14px;
}

.header-right .btn-primary {
  background: #4a6cf7;
  border-color: #4a6cf7;
}
.header-right .btn-primary:hover {
  background: #3a5cd9;
  border-color: #3a5cd9;
}

/* ============================================
   BODY – glass
   ============================================ */
.notification-body {
  display: flex;
  min-height: 600px;
  background: rgba(248, 250, 255, 0.5);
  backdrop-filter: blur(4px);
}

/* ============================================
   SIDEBAR – glass
   ============================================ */
.notification-sidebar {
  width: 260px;
  min-width: 260px;
  background: rgba(242, 246, 253, 0.55);
  backdrop-filter: blur(4px);
  padding: 20px 16px;
  border-right: 1px solid rgba(220, 226, 236, 0.3);
  overflow-y: auto;
}

.sidebar-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  color: #2f4057;
  padding: 8px 12px 8px;
  letter-spacing: 0.4px;
}

.sidebar-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #1f2a3e;
  font-size: 15px;
  gap: 12px;
  position: relative;
}

.sidebar-item:hover {
  background: rgba(220, 230, 245, 0.6);
  color: #1f3a7c;
}

.sidebar-item.active {
  background: rgba(220, 230, 245, 0.7);
  color: #1f3a7c;
  font-weight: 600;
}

.sidebar-icon {
  font-size: 18px;
  color: #3e5670;
  width: 24px;
  text-align: center;
}

.sidebar-item.active .sidebar-icon {
  color: #1f3a7c;
}

.sidebar-item span {
  flex: 1;
  color: #1f2a3e;
}

.sidebar-item.active span {
  color: #1f3a7c;
  font-weight: 600;
}

.badge-count {
  background: rgba(208, 219, 234, 0.7);
  color: #1f2a3e;
  font-size: 13px;
  padding: 2px 10px;
  border-radius: 30px;
  min-width: 24px;
  text-align: center;
  font-weight: 600;
}

.badge-count.danger {
  background: rgba(253, 226, 226, 0.8);
  color: #b02a37;
}

.sidebar-arrow {
  transition: transform 0.2s ease;
  font-size: 14px;
  color: #3e5670;
}

.sidebar-arrow.rotated {
  transform: rotate(90deg);
}

.sidebar-divider {
  height: 1px;
  background: rgba(208, 219, 234, 0.4);
  margin: 10px 12px;
}

/* Preferences – glass */
.preferences-panel {
  padding: 14px 14px 0;
}

.pref-item {
  padding: 6px 0;
}

.pref-label {
  font-size: 14px;
  color: #1f2a3e;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
}

.pref-label input[type="checkbox"] {
  cursor: pointer;
  width: 18px;
  height: 18px;
  accent-color: #4a6cf7;
}

/* ============================================
   NOTIFICATION LIST – glass
   ============================================ */
.notification-list {
  flex: 1;
  background: rgba(251, 253, 255, 0.5);
  backdrop-filter: blur(4px);
  overflow-y: auto;
}

/* Loading / Empty */
.list-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #2f4057;
  font-size: 16px;
}

.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
}

.empty-icon {
  color: rgba(184, 200, 221, 0.8);
  margin-bottom: 20px;
}

.list-empty h5 {
  color: #0b1a30;
  font-size: 20px;
  margin-bottom: 6px;
  font-weight: 600;
}

.list-empty .text-muted {
  color: #4a5f7a !important;
  font-size: 16px;
}

/* Notification Items */
.list-items {
  padding: 0;
}

.notification-item-wrapper {
  border-bottom: 1px solid rgba(228, 234, 242, 0.5);
  transition: all 0.15s ease;
}

.notification-item-wrapper.unread {
  background: rgba(238, 244, 255, 0.6);
  border-left: 4px solid #4a6cf7;
}

.notification-item-wrapper:hover {
  background: rgba(240, 245, 254, 0.6);
}

.notification-item {
  display: flex;
  padding: 20px 28px;
  gap: 18px;
  align-items: flex-start;
}

/* Icon */
.notification-icon {
  flex-shrink: 0;
  padding-top: 2px;
}

.icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: white;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}

.type-inventory { background: #e07c2e; }
.type-order { background: #3a7fc9; }
.type-refund { background: #3d9e6a; }
.type-return { background: #8b6ccf; }
.type-system { background: #5a6f88; }

/* Content */
.notification-content {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.notification-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
}

.notification-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.title-text {
  font-weight: 600;
  color: #0b1a30;
  font-size: 16px;
}

.unread-badge {
  background: #4a6cf7;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 12px;
  border-radius: 30px;
  text-transform: uppercase;
}

.priority-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 12px;
  border-radius: 30px;
  text-transform: uppercase;
}
.priority-badge.high {
  background: rgba(253, 226, 226, 0.8);
  color: #b02a37;
}
.priority-badge.medium {
  background: rgba(254, 247, 221, 0.8);
  color: #9e7a2b;
}
.priority-badge.normal {
  background: rgba(220, 230, 245, 0.7);
  color: #1f3a7c;
}
.priority-badge.low {
  background: rgba(228, 234, 242, 0.6);
  color: #3e5670;
}

.notification-time {
  font-size: 13px;
  color: #4a5f7a;
  white-space: nowrap;
}

.notification-message {
  font-size: 15px;
  color: #1f2a3e;
  margin-top: 6px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  gap: 14px;
}

.type-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 14px;
  border-radius: 30px;
  text-transform: uppercase;
}

.type-badge.inventory {
  background: rgba(244, 229, 213, 0.7);
  color: #a5561e;
}
.type-badge.order {
  background: rgba(220, 230, 245, 0.7);
  color: #1f4a7a;
}
.type-badge.refund {
  background: rgba(216, 240, 227, 0.7);
  color: #1f6a47;
}
.type-badge.return {
  background: rgba(233, 222, 247, 0.7);
  color: #5a3d8a;
}
.type-badge.system {
  background: rgba(228, 234, 242, 0.6);
  color: #2f4057;
}

.notification-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.action-btn {
  color: #4a6cf7;
  font-size: 14px;
  padding: 0;
  border: none;
  background: transparent;
  font-weight: 600;
}
.action-btn:hover {
  color: #2b4fcf;
}

.dropdown-actions >>> .dropdown-toggle {
  color: #4a5f7a;
  padding: 4px 8px;
  font-size: 16px;
}
.dropdown-actions >>> .dropdown-toggle:hover {
  color: #0b1a30;
}

/* Pagination */
.list-pagination {
  padding: 20px 28px;
  border-top: 1px solid rgba(228, 234, 242, 0.5);
  background: rgba(248, 250, 255, 0.4);
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 992px) {
  .notification-body {
    flex-direction: column;
  }
  .notification-sidebar {
    width: 100%;
    min-width: unset;
    border-right: none;
    border-bottom: 1px solid rgba(220, 226, 236, 0.3);
    padding: 16px 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .sidebar-section {
    flex: 1;
    min-width: 150px;
  }
  .sidebar-divider {
    display: none;
  }
  .notification-item {
    padding: 16px 20px;
  }
}

@media (max-width: 576px) {
  .notification-header {
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
    padding: 18px;
  }
  .header-right {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .notification-sidebar {
    padding: 12px 16px;
    flex-direction: column;
  }
  .sidebar-section {
    min-width: unset;
  }
  .notification-item {
    flex-direction: column;
    padding: 14px;
    gap: 10px;
  }
  .notification-icon {
    padding-top: 0;
  }
  .notification-header-content {
    flex-wrap: wrap;
  }
  .notification-title {
    flex-wrap: wrap;
  }
  .notification-footer {
    flex-wrap: wrap;
  }
  .header-right .btn {
    font-size: 13px;
    padding: 6px 12px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Notifications/NotificationPage.vue